import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { AuthenticationContext } from '../shared/contexts/authentication.context';
import { CurrentUser, Role, SiteSummary, ZpmPrivilege } from '../shared/models/generated/ziroModels';
import { ZiroIcons } from '../shared/models/ziro-icons.enum';
import { ButtonStyles } from '../button/button.component';
import { CurrentDateContext } from '../shared/contexts/current-date.context';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { Moment } from 'moment';
import { Router } from '@angular/router';
import { SearchFieldConfigContext } from '../shared/contexts/search-field-config.context';
import { HelpdeskOptionsContext } from '../helpdesk/shared/contexts/helpdesk-options.context';
import { SiteSummaryContext } from '../shared/contexts/site-summary.context';
import { GlobalPropertiesContext } from '../shared/contexts/global-properties.context';
import { ZiroModalService } from '../shared/services/ziro-modal.service';
import {
  ReleaseNotesModalComponent,
  ReleaseNotesModalViewProperties,
} from '../shared/release-notes/release-notes-modal.component';
import { ReleaseNotesService } from '../shared/release-notes/release-notes.service';
import { CustomUiOptionsContext } from '../shared/contexts/custom-ui-options.context';
import { EulaContext } from '../shared/eula/eula.context';
import { first, switchMap, tap } from 'rxjs/operators';
import { MicrosoftTenantsStateContext } from '../shared/contexts/microsoft-tenants-state.context';
import { SearchService } from '../helpdesk/search/search.service';

@Component({
  selector: 'ziro-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  standalone: false,
})
export class HomeComponent implements OnInit, AfterViewInit, OnDestroy {
  private authenticationContext = inject(AuthenticationContext);
  private globalPropertiesContext = inject(GlobalPropertiesContext);
  private helpdeskOptionsContext = inject(HelpdeskOptionsContext);
  private currentDateContext = inject(CurrentDateContext);
  private router = inject(Router);
  private siteSummaryContext = inject(SiteSummaryContext);
  private ziroModalService = inject(ZiroModalService);
  private releaseNotesService = inject(ReleaseNotesService);
  private uiOptionsContext = inject(CustomUiOptionsContext);
  private _eulaContext = inject(EulaContext);
  private searchFieldConfigContext = inject(SearchFieldConfigContext);
  private _microsoftTenantsStateContext = inject(MicrosoftTenantsStateContext);
  private _searchService = inject(SearchService);
  private _changeDetectorRef = inject(ChangeDetectorRef);

  @ViewChild('searchInput') searchInputRef: ElementRef;

  private _resizeObserver: ResizeObserver;

  buttonStyle = ButtonStyles;
  ziroIcons = ZiroIcons;
  buildVersion: string;
  buildTimestamp: string;
  isAddUserEnabled: boolean;
  isAdminOrGlobalHelpdesk: boolean;
  isAdminOrHelpdesk: boolean;
  isAdmin: boolean;
  year: number;
  isLoading = true;
  isCucmEnabled: boolean;
  isHosted: boolean;
  searchText: string;
  searchPlaceholder: string;
  isDeskphoneEnabledOnAnyClusters: boolean;
  zpmSearchErrorMessage: string;
  hideSearchErrorMessage: boolean;
  provisioningEnabled = false;
  proxyServer = false;
  releaseNotesUrl = 'https://learn.goziro.com/c/release-notes/5';
  isCreatingResourceAccountEnabled = false;
  showSupportLink = false;

  get selectedTenant(): string | null {
    return this._selectedTenantId;
  }

  private _isReleaseModalOpen = false;
  private _currentUser: CurrentUser;
  private _selectedTenantId: string | null;
  private _subscriptions = new Subscription();

  ngOnInit(): void {
    const contextSub = combineLatest([
      this.authenticationContext.state$,
      this.helpdeskOptionsContext.state$,
      this.currentDateContext.state$,
      this.searchFieldConfigContext.state$,
      this.globalPropertiesContext.state$,
      this.uiOptionsContext.state$,
    ]).subscribe(([user, helpdeskOptions, currentDateContextState, searchFieldConfig, globalProperties, uiOptions]) => {
      if (searchFieldConfig) {
        this.isCucmEnabled = searchFieldConfig.cucmSearchEnabled;
      }
      this.isAddUserEnabled =
        this.helpdeskOptionsContext.isCiscoHelpdeskOptions(helpdeskOptions) && helpdeskOptions.showAddUserPage;
      this._currentUser = user;
      this.buildVersion = globalProperties.version;
      this.buildTimestamp = globalProperties.buildTimestamp;
      this.isHosted = globalProperties.hostedEnabled;
      this.isAdminOrGlobalHelpdesk = this.authenticationContext.userIsAtLeast(
        this._currentUser,
        this.isHosted ? Role.S8_HELPDESK : Role.S8_GLOBAL_HELPDESK,
      );
      this.isAdminOrHelpdesk = this.authenticationContext.userIsAtLeast(this._currentUser, Role.S8_SITE_BASED_HELPDESK);
      this.isAdmin = this.isHosted
        ? this.authenticationContext.userHasPrivilege(this._currentUser, ZpmPrivilege.GLOBAL_SETTINGS)
        : this.authenticationContext.userIsAtLeast(this._currentUser, Role.S8_ADMIN);
      this.year = (currentDateContextState.utcTimestamp as Moment).year();

      this.provisioningEnabled = uiOptions.provisioningEnabled;
      this.proxyServer = uiOptions.proxyServerOnlyModeEnabled;
      this.showSupportLink = !!uiOptions?.supportTextHtml?.trim();

      this._displayEulaAndReleaseNotesModals();

      if (this.isCucmEnabled) {
        this._getSiteSummaryContextState();
      } else {
        this.isLoading = false;
      }
      this.getSearchPlaceholder();
    });
    this._subscriptions.add(contextSub);

    const tenantSub = this._microsoftTenantsStateContext.cachedTenantState$.subscribe((cachedTenant) => {
      this._selectedTenantId = cachedTenant?.tenantId ? cachedTenant.tenantId.toString() : null;
      this.isCreatingResourceAccountEnabled = cachedTenant?.creatingResourceAccountEnabled ?? false;
      this.getSearchPlaceholder();
    });

    this._subscriptions.add(tenantSub);
  }

  ngAfterViewInit(): void {
    this._resizeObserver = new ResizeObserver(() => this.getSearchPlaceholder());
    this._resizeObserver.observe(this.searchInputRef.nativeElement);
    this.getSearchPlaceholder();
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
    this._resizeObserver?.disconnect();
  }

  searchByString(): void {
    const query = this.searchText?.trim() ?? '';
    if (!this.isHosted) {
      this.router.navigate(['/search'], { queryParams: { query: query } });
    } else if (query.length < 3) {
      this.zpmSearchErrorMessage = 'tkey;home.search.error.text';
    } else if (!this._selectedTenantId) {
      this.zpmSearchErrorMessage = 'tkey;home.search.error.tenants';
    } else {
      this.router.navigate(['/helpdesk', 'tenants', this._selectedTenantId, 'search'], {
        queryParams: { query: query },
      });
    }
  }

  updateSearchError(): void {
    this.hideSearchErrorMessage = this.searchText && this.searchText.trim().length > 2 && !!this._selectedTenantId;
  }

  isResourceAccountManagementAllowed(): boolean {
    return this.authenticationContext.userHasPrivilege(
      this._currentUser,
      ZpmPrivilege.RESOURCE_ACCOUNT_MANAGEMENT,
      this._selectedTenantId,
    );
  }

  isCallQueueManagementAllowed(): boolean {
    return this.authenticationContext.userHasPrivilege(
      this._currentUser,
      ZpmPrivilege.CALL_QUEUE_MANAGEMENT,
      this._selectedTenantId,
    );
  }

  isEmergencyCallingManagementAllowed(): boolean {
    return this.authenticationContext.userHasPrivilege(
      this._currentUser,
      ZpmPrivilege.EMERGENCY_LOCATION_MANAGEMENT,
      this._selectedTenantId,
    );
  }

  isAutoAttendantManagementAllowed(): boolean {
    return this.authenticationContext.userHasPrivilege(
      this._currentUser,
      ZpmPrivilege.AUTO_ATTENDANT_MANAGEMENT,
      this._selectedTenantId,
    );
  }

  hasSupportSettingsPrivilege(): boolean {
    return this.authenticationContext.userHasPrivilege(this._currentUser, ZpmPrivilege.ZIRO_SUPPORT_SETTINGS);
  }

  shouldShowSearchBar(): boolean {
    return this._searchService.shouldShowSearch(this.isHosted, this._currentUser, this._selectedTenantId);
  }

  getSearchPlaceholder(): void {
    setTimeout(() => {
      const input = document.getElementById('home-search-input');
      if (!input || input.clientWidth === 0) {
        return;
      }
      this.searchPlaceholder = this._searchService.getSearchPlaceholder(
        this.isHosted,
        input,
        this._currentUser,
        this._selectedTenantId,
      );
      this._changeDetectorRef.detectChanges();
    });
  }

  private _getSiteSummaryContextState(): void {
    const siteSummaryContextSubscription = this.siteSummaryContext.state$.subscribe((state: SiteSummary) => {
      this.isDeskphoneEnabledOnAnyClusters = this.siteSummaryContext.isDeskphoneEnabledOnAnyClusters(state);
      this.isLoading = false;
    });
    this._subscriptions.add(siteSummaryContextSubscription);
  }

  private _displayEulaAndReleaseNotesModals(): void {
    this.releaseNotesService.migrateLocalStorage();
    const sub = this._eulaContext.state$
      .pipe(
        first(),
        switchMap(() => this._checkReleaseNotes()),
        switchMap((releaseNote) => this._displayReleaseNotesModal(releaseNote)),
      )
      .subscribe(() => {
        this._isReleaseModalOpen = false;
      });

    this._subscriptions.add(sub);
  }

  private _displayReleaseNotesModal(releaseNote: ReleaseNotesModalViewProperties): Observable<void> {
    if (releaseNote && !this._isReleaseModalOpen) {
      this._isReleaseModalOpen = true;
      this.releaseNotesUrl = `https://learn.goziro.com/t/${releaseNote.slug}`;
      return this.ziroModalService
        .openCustomModal(
          ReleaseNotesModalComponent,
          {
            html: releaseNote.html,
            title: releaseNote.title,
            slug: releaseNote.slug,
          },
          { size: 'lg' },
        )
        .pipe(tap(() => this.releaseNotesService.setViewedReleaseNotesId()));
    } else {
      return of(null);
    }
  }

  private _checkReleaseNotes(): Observable<ReleaseNotesModalViewProperties | null> {
    if (!this.isHosted) {
      return of(null);
    }
    return this.releaseNotesService.getLatestReleaseNotes();
  }

  protected readonly String = String;

  protected readonly buttonStyles = ButtonStyles;
  protected readonly ZiroIcons = ZiroIcons;
}
